import { useCallback, useEffect, useRef, useState } from "react";
import {
	isIndiaCountry,
	loadStateOptions,
	toCountrySelectValue,
	toStateSelectValue,
} from "../Services/masterCountriesService";

/**
 * Reusable country + state selection.
 * - No default country
 * - Loads states only after a country is selected
 * - Passes selected country code/name to masterStates (never hardcodes a country)
 */
export const useCountryStateSelect = ({ enabled = true } = {}) => {
	const [country, setCountry] = useState(null);
	const [state, setState] = useState(null);
	const [states, setStates] = useState([]);
	const [statesLoading, setStatesLoading] = useState(false);
	const requestIdRef = useRef(0);

	useEffect(() => {
		if (!enabled || !country) {
			setStates([]);
			setStatesLoading(false);
			return undefined;
		}

		const requestId = ++requestIdRef.current;
		let cancelled = false;

		const fetchStates = async () => {
			setStatesLoading(true);
			try {
				const options = await loadStateOptions(country);
				if (!cancelled && requestId === requestIdRef.current) {
					setStates(options);
				}
			} catch {
				if (!cancelled && requestId === requestIdRef.current) {
					setStates([]);
				}
			} finally {
				if (!cancelled && requestId === requestIdRef.current) {
					setStatesLoading(false);
				}
			}
		};

		fetchStates();

		return () => {
			cancelled = true;
		};
	}, [country, enabled]);

	const onCountryChange = useCallback((option) => {
		setCountry(option || null);
		setState(null);
		setStates([]);
	}, []);

	const onStateChange = useCallback((option) => {
		setState(option || null);
	}, []);

	const hydrate = useCallback(({ country: countryName, state: stateName, stateCode } = {}) => {
		setCountry(toCountrySelectValue(countryName));
		setState(toStateSelectValue(stateName, stateCode));
	}, []);

	const reset = useCallback(() => {
		requestIdRef.current += 1;
		setCountry(null);
		setState(null);
		setStates([]);
		setStatesLoading(false);
	}, []);

	return {
		country,
		state,
		states,
		statesLoading,
		showStateCode: isIndiaCountry(country),
		countryName: country?.label || "",
		stateName: state?.label || "",
		stateCode: isIndiaCountry(country) ? state?.gstCode || "" : "",
		onCountryChange,
		onStateChange,
		hydrate,
		reset,
	};
};

export default useCountryStateSelect;
